/* eslint-disable */

import { Button, Checkbox, Col, Divider, Input, Radio, Result, Row, Select, Switch, Tooltip } from 'antd';
import { CheckOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import usePlacesService from "react-google-autocomplete/lib/usePlacesAutocompleteService";

import { InvestorAPI } from '../../../Api/InvestorAPI';
import { ContractAPI } from '../../../Api/ContractAPI';

import { usePhysicalInvestorForm } from '../../../Hooks/usePhysicalInvestorForm/usePhysicalInvestorForm';

import FormLayout from '../../FormLayout/FormLayout';
import CountryField from '../../Fields/CountryField/CountryField';
import DateField from '../../Fields/DateField/DateField';
import TextField from '../../Fields/TextField/TextField';
import DocumentExpirationField from '../../Fields/DocumentExpirationField/DocumentExpirationField';
import TaxIdentificationField from '../../Fields/TaxIdentificationField/TaxIdentificationField';
import DocumentTypeField from '../../Fields/DocumentTypeField/DocumentTypeField';
import SectorField from '../../Fields/SectorField/SectorField';
import FundsSourceField from '../../Fields/FundsSourceField/FundsSourceField';

import { IPhysicalInvestorForm } from './IPhysicalInvestorForm';
import { InvestorDTO } from '../../../Models';
import {
	ACTIVITY_CANONICAL_OPTIONS,
	AMOUNT_CANONICAL_OPTIONS,
	JOB_TITLE_CANONICAL_OPTIONS,
	PHYSICAL_SOURCE_CANONICAL_OPTIONS,
	SECTOR_CANONICAL_OPTIONS,
	getActivityOptions,
	getAmountOptions,
	getJobTitleOptions,
	getPhysicalSourceOptions,
	getSectorOptions,
} from '../../../Assets/OptionsTranslations';

import './PhysicalInvestorForm.scss';

const { Option } = Select;

const PhysicalInvestorForm = ({
	baseURL,
	baseURLExternal,
	translations,
	clientId,
	unlockNextStep,
	language,
	contractLanguage,
	isInvestorWithoutNIFEnabled,
	isReadOnly = false
}: IPhysicalInvestorForm) => {
	const investorAPI = InvestorAPI(baseURL, baseURLExternal, clientId);
	const contractAPI = ContractAPI(baseURL, baseURLExternal, clientId);

	const {
		placesService,
		placePredictions,
		getPlacePredictions,
		isPlacePredictionsLoading,
	} = usePlacesService({ apiKey: "AIzaSyCU9qZlMZtXtxKvRJi-NRNJtPJF0p7DpYo" });

	const {
		loading,
		noContent,
		errors,
		hasErrors,
		investorsData,
		nationalitiesList,
		activityInList,
		contractId,
		mainAddressInputs,
		taxAddressInputs,
		manualAddressVisibility,
		activeAddress,
		defaultValues,
		onChangeDate,
		handleChangeData,
		handleSelectData,
		handleChangeDate,
		handleCheckedData,
		saveData,
		onChangeSameAddresses,
		handleSelectAddress,
		handleSearchAddress,
		onChangeHasPRP,
		onChangeNotificationsEnabled,
		isEmployeeSituation
	} = usePhysicalInvestorForm({
		translations,
		investorAPI,
		contractAPI,
		placesService,
		unlockNextStep,
		getPlacePredictions,
	});

	const activityOptions = getActivityOptions(language);
	const physicalSourceOptions = getPhysicalSourceOptions(language);
	const sectorOptions = getSectorOptions(language);
	const amountOptions = getAmountOptions(language);
	const jobTitleOptions = getJobTitleOptions(language);

	return (
		<FormLayout 
			translations={translations}
			loading={loading}
			title={translations.formularioInversor}
			subTitle={translations.encabezadoFormulario}
			onClick={saveData}
			disabledSaveButton={noContent}
			contractId={contractId}
			nextStep={unlockNextStep}
			enableNextStep={!hasErrors}
			forceEnableNextStep={isReadOnly}
			hideSaveButton={isReadOnly}
		>	
			<>
				{!noContent ?
					(
						investorsData.map((investorData: InvestorDTO, index: number) => (
							<>
								<h3>
									{translations.datosFirmante} {index + 1}
								</h3>
								<Row gutter={[16, 16]}>
									<DateField
										value={investorData.birthDate}
										fieldName="birthDate"
										required
										translations={translations}
										errors={errors}
										index={index}
										handleDatePicker={onChangeDate}
										disabled={isReadOnly}
									/>
									<TextField
										size='s'
										placeholder={translations.placeholderLugarNac}
										value={investorData.birthPlace}
										fieldName="birthPlace"
										translations={translations}
										required
										errors={errors}
										index={index}
										handleChange={handleChangeData}
										disabled={isReadOnly}
									/>
									<CountryField
										value={investorData.nationality}
										fieldName="nationality"
										nationalitiesList={nationalitiesList}
										translations={translations}
										errors={errors}
										index={index}
										handleSelect={handleSelectData}
										language={language}
										contractLanguage={contractLanguage}
										disabled={isReadOnly}
									/>
									<DocumentTypeField
										value={investorData.documentType!}
										index={index}
										translations={translations}
										errors={errors}
										handleSelect={handleSelectData}
										disabled={isReadOnly}
									/>
									<Col xs={12} md={6} lg={6}>
										<label className="required">
											{translations.documentNumber}
											<Tooltip placement="top" title={<span>{translations.documentNumberTooltip}</span>}>
												{" "} <InfoCircleOutlined />
											</Tooltip>
										</label>
										<Input
											className="input-field"
											value={investorData.dni || investorData.nif || investorData.passport || investorData.residenceCard}
											disabled={isReadOnly}
											readOnly={isReadOnly}
											onChange={handleChangeData(index)}
											placeholder={translations.documentNumber}
											status={errors[index].document ? "error" : "" }
										/>
										<span className={`error ${errors[index].document ? 'show' : null}`}>{translations.completeCampo}</span>
										<span className={`error ${errors[index].documentFormat ? 'show' : null}`}>{translations.invalidIdDocument}</span>
									</Col>
									<DocumentExpirationField
										documentExpiration={investorData.docValidThru}
										translations={translations}
										errors={errors}
										fieldName="docValidThru"
										index={index}
										handleDatePicker={handleChangeDate}
										disabled={isReadOnly}	
									/>
									<TextField
										size='s'
										value={investorData.phonePrefix}
										fieldName="phonePrefix"
										translations={translations}
										required
										errors={errors}
										index={index}
										handleChange={handleChangeData}
										disabled={isReadOnly}
									/>
									<TextField
										size='s'
										value={investorData.phoneNumber}
										fieldName="phoneNumber"
										translations={translations}
										required
										errors={errors}
										index={index}
										handleChange={handleChangeData}
										disabled={isReadOnly}
									/>
									<TextField
										size='m'
										value={investorData.emailContact}
										fieldName="emailContact"
										translations={translations}
										required
										errors={errors}
										index={index}
										handleChange={handleChangeData}
										disabled={isReadOnly}
									/>
									<TaxIdentificationField
										placeholder={translations.taxIdentification}
										value={investorData.taxIdentification}
										fieldName="taxIdentification"
										translations={translations}
										errors={errors}
										index={index}
										handleChange={handleChangeData}
										disabled={isReadOnly}
									/>
								</Row>
								<Row gutter={[16, 16]} justify="start" style={{ width: "110%" }}>
									<Col xs={24} md={12} lg={12} className="titles">
										<label className="required"> {translations.mainResidence} </label>
										<Select
											className="input-field"
											style={{ width: "100%" }}
											showSearch
											value={mainAddressInputs[index]}
											placeholder={translations.direccion}
											disabled={isReadOnly}
											open={isReadOnly ? false : undefined}
											onSearch={handleSearchAddress(index, 'mainResidence')}
											onChange={handleSelectAddress(index, 'mainResidence')}
											filterOption={false} // Importante: para que solo muestre las opciones de Google
											notFoundContent={isPlacePredictionsLoading ? "Loading..." : null}
										>
											{activeAddress && activeAddress.index === index && activeAddress.field === "mainResidence"
												? placePredictions.map((item) => (
													<Option key={item.place_id} value={item.description}>
													{item.description}
													</Option>
												))
												: null
											}
										</Select>
										<span className={`error ${errors[index].mainResidence ? 'show' : null}`}>{translations.completeCampo}</span>
									</Col>
									<TextField
										size='m'
										value={investorData.mainResidenceExtra}
										fieldName="mainResidenceExtra"
										placeholder={translations.mainResidenceExtra}
										translations={translations}
										required={false}
										index={index}
										errors={errors}
										handleChange={handleChangeData}
										disabled={isReadOnly}
									/>
								</Row>
								{manualAddressVisibility[index]?.mainResidence && (
									<>
										<TextField
											size='m'
											label={translations.direccion}
											value={investorData.mainResidence}
											fieldName="mainResidence"
											errorWhenEmpty
											translations={translations}
											required
											errors={errors}
											index={index}
											handleChange={handleChangeData}
										/>
										<TextField
											size='s'
											value={investorData.mainResidencePostalCode}
											fieldName="mainResidencePostalCode"
											translations={translations}
											required
											errors={errors}
											index={index}
											handleChange={handleChangeData}
										/>
										<TextField
											size='s'
											value={investorData.mainResidenceCity}
											fieldName="mainResidenceCity"
											translations={translations}
											required
											errors={errors}
											index={index}
											handleChange={handleChangeData}
										/>
										<CountryField
											value={investorData.mainResidenceCountry}
											fieldName="mainResidenceCountry"
											nationalitiesList={nationalitiesList}
											translations={translations}
											errors={errors}
											index={index}
											handleSelect={handleSelectData}
											language={language}
											contractLanguage={contractLanguage}
										/>
									</>
								)}
								<Row gutter={[16, 16]} style={{ width: "110%" }}>
									<Col span="24">
										<label> {translations.taxResidenceEqualsSocialAddress}     </label>
										<Switch
											checked={investorData.sameAddresses}
											checkedChildren={<CheckOutlined />}
											unCheckedChildren={<CloseOutlined />}
											disabled={isReadOnly}
											onChange={onChangeSameAddresses(index)}
										/>
									</Col>
									{!investorData.sameAddresses
										? <>
											<Col xs={24} md={12} lg={12}>
												<label className="required"> {translations.taxResidence} </label>
												<Select
													style={{ width: "100%" }}
													showSearch
													value={taxAddressInputs[index]}
													placeholder={translations.direccion}
													disabled={isReadOnly}
													open={isReadOnly ? false : undefined}
													onSearch={handleSearchAddress(index, 'taxResidence')}
													onChange={handleSelectAddress(index, 'taxResidence')}
													filterOption={false} // Importante: para que solo muestre las opciones de Google
													notFoundContent={isPlacePredictionsLoading ? "Loading..." : null}
												>
													{activeAddress && activeAddress.index === index && activeAddress.field === "taxResidence"
														? placePredictions.map((item) => (
															<Option key={item.place_id} value={item.description}>
															{item.description}
															</Option>
														))
														: null
													}
												</Select>
												<span className={`error ${errors[index].taxResidence ? 'show' : null}`}>{translations.completeCampo}</span>
											</Col>
											<TextField
												size='m'
												value={investorData.taxResidenceExtra}
												fieldName="taxResidenceExtra"
												placeholder={translations.taxResidenceExtra}
												translations={translations}
												required={false}
												index={index}
												errors={errors}
												handleChange={handleChangeData}
												disabled={isReadOnly}
											/>
											{manualAddressVisibility[index]?.taxResidence && (
												<>
													<TextField
														size='m'
														label={translations.direccion}
														value={investorData.taxResidence}
														fieldName="taxResidence"
														errorWhenEmpty
														translations={translations}
														required
														errors={errors}
														index={index}
														handleChange={handleChangeData}
													/>
													<TextField
														size='s'
														value={investorData.taxResidencePostalCode}
														fieldName="taxResidencePostalCode"
														translations={translations}
														required
														errors={errors}
														index={index}
														handleChange={handleChangeData}
													/>
													<TextField
														size='s'
														value={investorData.taxResidenceCity}
														fieldName="taxResidenceCity"
														translations={translations}
														required
														errors={errors}
														index={index}
														handleChange={handleChangeData}
													/>
													<CountryField
														value={investorData.countryTaxResidence}
														fieldName="countryTaxResidence"
														nationalitiesList={nationalitiesList}
														translations={translations}
														errors={errors}
														index={index}
														handleSelect={handleSelectData}
														language={language}
														contractLanguage={contractLanguage}
													/>
												</>
											)}
										</> : null
									}
								</Row>
								{isInvestorWithoutNIFEnabled
									? <Row gutter={[16, 16]}>
										<Col xs={24} md={24} lg={24} className="titles">
											<label>{translations.encabezadoRazon}</label>
											<Row className="nif-radio-group">
												<Radio.Group
													name="notNIFReason"
													value={investorData.notNIFReason}
													disabled={isReadOnly}
													onChange={handleChangeData(index)}
												>
													<Radio className="nif-radio" value={1}> {translations.razonA}</Radio>
													<Radio className="nif-radio" value={2}>{translations.razonB}</Radio>
													{/*  Este input, sólo se habilita si se selecciona la Razón B */}
													<Input
														className="nif-radio-reason"
														placeholder={translations.placeHolderRazon}
														disabled={isReadOnly || investorData.notNIFReason !== 2}
														readOnly={isReadOnly}
														name="notNIFDetailReason"
														value={investorData.notNIFDetailReason}
														onChange={handleChangeData(index)}
													/>
													<Radio className="nif-radio" value={3}>{translations.razonC}</Radio>
												</Radio.Group>
											</Row>
										</Col>
									</Row>
									: null
								}
								<Row gutter={[16, 16]}>
									<Col xs={24} md={12} lg={12} className="titles">
										<label className="required"> {translations.employmentSituation}</label>
										<Select
											className="input-field"
											value={investorData.situation}
											onChange={handleSelectData('situation', index)}
											disabled={isReadOnly}
											status={errors[index].situation ? 'error' : ''}
											placeholder={translations.seleccioneOpcion}
										>
											{activityOptions.map((option: string, idx: number) => (
												<Option key={idx} value={ACTIVITY_CANONICAL_OPTIONS[idx] || option}>{option}</Option>
											))}
										</Select>
										<span className={`error ${errors[index].situation ? 'show' : null}`}>{translations.seleccioneOpcion}</span>
									</Col>
										{isEmployeeSituation(investorData.situation)
											? <TextField
												size='m'
												value={investorData.employerName}
												fieldName="employerName"
												translations={translations}
												required
												errors={errors}
												index={index}
												handleChange={handleChangeData}
												disabled={isReadOnly}
											/>
											: null
										}
									<FundsSourceField
										value={ investorData.source }
										fieldName='source'
										index={ index }
										translations={ {...translations, sourceOptions: physicalSourceOptions } }
										canonicalValues={PHYSICAL_SOURCE_CANONICAL_OPTIONS}
										errors={ errors }
										handleSelectOrDatePicker={ handleSelectData }
										disabled={isReadOnly}
									/>
									<>
										<SectorField
											value={investorData.sector}
											fieldName="sector"
											index={index}
											translations={{...translations, sectorOptions}}
											canonicalValues={SECTOR_CANONICAL_OPTIONS}
											errors={errors}
											handleSelectOrDatePicker={handleSelectData}
											disabled={isReadOnly}
										/>
										{ 
											!activityInList 
												? <TextField
													size='m'
													value={ investorData.otherSector }
													fieldName="otherSector"
													placeholder={ translations.otherSector }
													translations={ translations }	
													required={ false }
													errors={ errors }
													index={ index }
													handleChange={ handleChangeData }
													disabled={isReadOnly}
												/>
												: null
										}
									</>										
									<Col xs={24} md={12} lg={12} className="titles">
										<label className="required"> {translations.anualIncome}</label>
										<Select
											className="input-field"
											value={investorData.incomes}
											onChange={handleSelectData('incomes', index)}
											disabled={isReadOnly}
											status={errors[index].incomes ? 'error' : ''}
											placeholder={translations.seleccioneOpcion}
										>
											{amountOptions.map((option: string, idx: number) => (
												<Option key={idx} value={AMOUNT_CANONICAL_OPTIONS[idx] || option}>{option}</Option>
											))}
										</Select>
										<span className={`error ${errors[index].incomes ? 'show' : null}`}>{translations.seleccioneOpcion}</span>
									</Col>
									<Col xs={24} md={12} lg={12} className="titles">
										<label className="required"> {translations.ocupation}</label>
										<Select
											className="input-field"
											value={investorData.ocupation}
											onChange={handleSelectData('ocupation', index)}
											disabled={isReadOnly}
											status={errors[index].ocupation ? 'error' : ''}
											placeholder={translations.seleccioneOpcion}
										>
											{jobTitleOptions.map((option: string, idx: number) => (
												<Option key={idx} value={JOB_TITLE_CANONICAL_OPTIONS[idx] || option}>{option}</Option>
											))}
										</Select>
										<span className={`error ${errors[index].ocupation ? 'show' : null}`}>{translations.seleccioneOpcion}</span>
									</Col>
									<Col xs={ 24 } md={ 8 } lg={ 8 } className="titles">
										<p>
											<label className="required"> {translations.isPRP} </label>
											<Tooltip placement="topLeft" title={translations.tooltipFamiliarPrp} >
												<InfoCircleOutlined className="tooltip" />
											</Tooltip>
										</p>
										<Switch
											checked={investorData.hasPRP}
											checkedChildren={<CheckOutlined />}
											unCheckedChildren={<CloseOutlined />}
											disabled={isReadOnly}
											onChange={onChangeHasPRP(index)}
										/>
									</Col>
									{ investorData.hasPRP
										? <Col xs={24} md={16} lg={16} className="titles">
											<p>
												<label className="required"> {translations.prpDetails} </label>
											</p>
											<Input type="text"
												name="prp"
												value={investorData.prp}
												disabled={isReadOnly}
												readOnly={isReadOnly}
												onChange={handleChangeData(index)}
												status={errors[index].prp ? 'error' : ''}
											/>
											<span className={`error ${errors[index].prp ? 'show' : null}`}>{translations.completeCampo}, de no corresponder escriba "NA"</span>
										</Col>
										: null
									}
{/* 									<Col xs={ 24 } md={ 8 } lg={ 8 } className="titles">
										<p>
											<label className="required"> {translations.signtoryNotifications} </label>
											<Tooltip placement="topLeft" title={translations.signtoryTooltipNotifications} >
												<InfoCircleOutlined className="tooltip" />
											</Tooltip>
										</p>
										<Switch
											checked={investorData.notificationsEnabled}
											checkedChildren={<CheckOutlined />}
											unCheckedChildren={<CloseOutlined />}
											onChange={onChangeNotificationsEnabled(index)}
										/>
									</Col> */}
								</Row>
								<Row className="titles">
									<Col xs={24} md={24} lg={24}>
										<p>
											<label> {translations.encabezadoFatca} </label>
											<Tooltip placement="topLeft" title={translations.tooltipFacta} >
												<InfoCircleOutlined className="tooltip" />
											</Tooltip>
										</p>
									</Col>
									<Col xs={24} md={24} lg={24}>
										<Checkbox
											className='qualitas-checkbox'
											name="american"
											checked={investorData.american}
											value= {investorData.american}
											disabled={isReadOnly}
											onChange={handleCheckedData(index)}
										>
											<label>{translations.estadounidense}</label>
										</Checkbox>
									</Col>
								</Row>
								<Divider />
								{/* Esta sección, se muestra una única vez */}
								{
									index === 0
										? (
										<Row gutter={[16, 16]}>
											<Col xs={24} md={12} lg={12} className="titles">
												<p>
													<label className="required"> {translations.bancoDepositos} </label>
													<Tooltip placement="topLeft" title={translations.tooltipbanco} >
														<InfoCircleOutlined className="tooltip" />
													</Tooltip>
												</p>
												<Input type="text"
													name="bankDeposits"
													value={investorData.bankDeposits}
													onChange={handleChangeData(index)}
													status={errors[index].bankDeposits ? 'error' : ''}
													disabled={isReadOnly || defaultValues.some(defVal => defVal.field === "bankDeposits" && defVal.value)}
													readOnly={isReadOnly}
												/>
												<span className={`error ${errors[index].bankDeposits ? 'show' : null}`}>{translations.completeCampo}</span>
											</Col>
											<TextField
												size="m"
												value={investorData.bankAddress}
												fieldName="bankAddress"
												translations={translations}
												required
												errors={errors}
												index={index}
												handleChange={handleChangeData}
												disabled={isReadOnly || defaultValues.some(defVal => defVal.field === "bankAddress" && defVal.value)}
											/>
											<TextField
												size="m"
												value={investorData.iban}
												fieldName="iban"
												translations={translations}
												required
												errors={errors}
												index={index}
												handleChange={handleChangeData}
												disabled={isReadOnly || defaultValues.some(defVal => defVal.field === "iban" && defVal.value)}
											/>
											<TextField
												size="m"
												value={investorData.biC_SWIFT}
												fieldName="biC_SWIFT"
												translations={translations}
												required
												errors={errors}
												index={index}
												handleChange={handleChangeData}
												disabled={isReadOnly || defaultValues.some(defVal => defVal.field === "biC_SWIFT" && defVal.value)}
											/>
										</Row>
									)
									: null
							}
							</>
						))
					)
					:
					<div className="no-content-wrapper">
						<Result
							status="warning"
							title="Este contrato no existe o no está disponible"
							extra={
							<Button type="primary" key="console">
								{translations.inicio}
							</Button>
							}
						/>
					</div>
				}
			</>
		</FormLayout>
	);
};

export default PhysicalInvestorForm;
